import { Button, Card, Checkbox, Col, DatePicker, Flex, Layout, Modal, notification, Radio, Result, Row, Skeleton, Typography } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { usePermissions } from "src/pages/features/usePermissions";
import dayjs, { Dayjs } from "dayjs";
import { HubConnectionBuilder, HubConnectionState, HubConnection, LogLevel } from "@microsoft/signalr";
import { useGetPremierPlusBinDto } from "src/queries/useGetPremierPlusBinDto";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@ant-design/pro-layout";
import Routes from "src/consts/Routes";
import { useBinInfoContext } from "src/queries/BinInfoContext";
import Overview from "./Overview";
import { ReadingOnOffDisplay } from "./ReadingOnOffDisplay";
import { DryerSettingsCard } from "./DryerSettingsCard";
import SystemControls from "src/components/Premier/PremierPlus/SystemControls";
import CustomTag from "src/components/CustomTag";
import { Charts } from "./Charts";
import { PremierPlusTapeStripTable } from "./PremierPlusTapeStripTable";
import { CameraImage } from "src/pages/dashboard/Camera/CameraImage";
import BinApiService from "src/api/BinApiService";
import FillingForcast from "src/pages/dashboard/BinStatusPage/FilingForecast";
import { CheckCircleTwoTone, CloseCircleTwoTone, RedoOutlined } from "@ant-design/icons";
import PremierPlusAlertTable from "./PremierPlusAlertTable";
import RoleUtil from "src/utils/RoleUtil";
import { DATETIME_FORMAT } from "./shared";
import ScheduleSampledSnapshots from "./ScheduleSampledSnapshots";
import BinInfoForIotDeviceDTO from "src/models/BinInfoForIotDeviceDTO";
import { AdminControlsCard } from "src/components/AdminControlsCard";
import AutomationType from "src/consts/AutomationType";
import { cycleAveragesQueryOptions, TAPESTRIP_CATEGORY } from "src/queries/getCycleAveragesPremierPlusOptions";
import { binDBKeys } from "src/pages/binOverview/BinCommander";

const EMPTY_DATE_RANGE: [Dayjs | null, Dayjs | null] = [null, null];
const ControlPanel = () => {

    const queryClient = useQueryClient();
    const history = useHistory();
    const location = useLocation();
    const binInfo = useBinInfoContext();
    const isAdmin = RoleUtil.currentUserIsAdmin();

    const  { getPremierPlusBinDtoQueryKey, getPremierPlusBinDtoQuery } = useGetPremierPlusBinDto(binInfo?.id, { enabled: true, refetchOnWindowFocus: false });

    const lastSnapshotCaptureTime = useMemo( () => {
        return getPremierPlusBinDtoQuery.data?.captureTimeUtc != null ? dayjs(getPremierPlusBinDtoQuery.data.captureTimeUtc) : null;
    },[getPremierPlusBinDtoQuery.data?.captureTimeUtc]);

    const [tapestripModalOpen, setTapestripModalOpen] = useState(false);
    const [alertModalOpen, setAlertModalOpen] = useState(false);
    const [alertFilter, setAlertFilter] = useState<1 | 2>(1); // 1: active only, 2: inactive only

    const handleOpenAlertModal = () => {
        setAlertModalOpen(true);
    }

    const handleCloseAlertModal = () => {
        setAlertModalOpen(false);
    }

    const handleOpenTapestripModal = () => {
        setTapestripModalOpen(true);
    }

    const handleCloseTapestripModal = () =>{
        setTapestripModalOpen(false);
    }

    const handleClickDiagnosticsFile = () => {
        try {
            const dto = getPremierPlusBinDtoQuery.data;
            const jsonData = dto?.snapshotJson;

            if (!jsonData) {
                console.error("No SnapshotJson found on PremerPlusBinDto.");
                notification.error({ message: 'Can not show diagnostics file'});
                return;
            }

            // Accept either a string or an object
            const text =
            typeof jsonData === "string" ? jsonData : JSON.stringify(jsonData, null, 2);

            // Build a friendly filename with timestamp (adjust bin/name if you have those fields)
            const ts = dayjs(jsonData.captureTimeUTC).format('MM/DD/YYYY HH:mm:ss');
            const filename = `diagnostics-${ts}.txt`;

            const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
            const url = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();

            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Failed to download diagnostics file:", err);
        }
    }

    const startSignalRConnection = useCallback(async (connection: HubConnection) => {
        try {
            await connection.start();
            console.log("signalR connected, ", "url: ", connection.baseUrl);
        } catch (err) {
            console.error("Error establishing signalr connection: ", err);
            setTimeout(() => startSignalRConnection(connection), 5000);
        }
    }, []);

    const [tapeStripDateRange, setTapeStripDateRange] = useState<[Dayjs | null, Dayjs | null]>([ dayjs().subtract(7, 'day'), dayjs()]);
    const [alertDateRange, setAlertDateRange] = useState<[Dayjs | null, Dayjs | null]>([ dayjs().subtract(7, 'day'), dayjs()]);
    
    useEffect(() => {
        const binId = binInfo?.id;
        if (binId == null) {
            return;
        }
        let connection: HubConnection;
        let disconnectedDueToUnmount = false;
    
        const setUpSignalRConnection = async (binId: number) => {

            const connection = new HubConnectionBuilder()
                .withUrl("/signalr")
                .withAutomaticReconnect()
                .configureLogging(LogLevel.Information)
                .build();

            connection.on("NewSnapshot", async (message: any) => {
                const binKey = binDBKeys.bin(binId);
                queryClient.invalidateQueries({ queryKey:[getPremierPlusBinDtoQueryKey] });
                queryClient.invalidateQueries({queryKey: [...binKey, {category: TAPESTRIP_CATEGORY}]});
                console.log('Requesting new BinDTO');
            });

            connection.on("NewSettings", async (message: any) => {
                queryClient.invalidateQueries({ queryKey:[getPremierPlusBinDtoQueryKey] });
                const binKey = binDBKeys.bin(binId);
                queryClient.invalidateQueries({queryKey: [...binKey, {category: TAPESTRIP_CATEGORY}]});
                console.log('Requesting new BinDTO');
            });

            // start the connection
            await startSignalRConnection(connection);
            // subscribe to bin

            if (connection.state === HubConnectionState.Connected) {
                try {
                    await connection.invoke("SubscribeBin", binId);
                    console.log("signalr: subscribed to binId: ", binInfo?.id);
                } catch (err) {
                    console.error(`signalr: Error subscribing to bin with Id ${binId}`, err.toString());
                }
            } else {
                console.log("hub not connected");
            }
    
            // handle disconnect
            connection.onclose(async error => {
                console.log("disconnected from SignalR", error);
                if (disconnectedDueToUnmount) {
                    console.debug("skipping reconnect due to component cleanup/umount");
                    return;
                }
                await startSignalRConnection(connection);
            });
    
            //return the connection
            return connection;
        };
    
        if (binId) {
            setUpSignalRConnection(binId).then(con => {
                connection = con;
            });
        }
    
        const cleanUpSignalRConnection = async (binId: number, connection: HubConnection) => {
            if (connection.state === HubConnectionState.Connected) {
                connection;
                try {
                    await connection.invoke("UnsubscribeBin", binId);
                    console.log("unsubscribing from bin", binId);
                } catch (err) {
                    return console.error("Error unsubscribing to bin", binId, err.toString());
                }
                connection.off("SubscribeBin");
                disconnectedDueToUnmount = true;
                connection.stop();
            } else {
                connection.off("SubscribeBin");
                disconnectedDueToUnmount = true;
                connection.stop();
            }
        };
    
        return function cleanUp() {
            if (binInfo?.id) {
                const binId = binInfo?.id;
                cleanUpSignalRConnection(binId, connection).catch(err => console.log("failed to cleanup signalr", err));
            }
        };
    }, [binInfo?.id]);

    const headerTags = useMemo(() => {
        const tags = [<CustomTag label={'Premier+'} color={'orange'} />];
        if(!getPremierPlusBinDtoQuery.data?.moduleConnected){
            tags.push(<CustomTag label={'OFFLINE'} color='red'/>);
        }
        tags.push(<CustomTag label={getPremierPlusBinDtoQuery.data?.iotDevice?.serialNumber ??'Unknown Serial' } color="orange"/>);
        return tags;
    }, [getPremierPlusBinDtoQuery.data]);

    const handleClickRefresh = async () => {
        try{
            const binId = getPremierPlusBinDtoQuery.data?.binId ?? -1;
            const result = await BinApiService.uploadPremierPlusSnapshot(binId);
            notification.success({ message: 'Successfuly refreshed bin' });
        }catch(err){
            notification.error({ message: 'Could not refresh bin' });
        }
    }

    if(binInfo.id == null){
        return(
            <p> No bin found</p>
        )
    }

    return (
        <>
            <Layout.Content className='dashboard'>
                <Row justify={'space-between'} align='bottom'>
                    <Col>
                        <Row align='middle' gutter={8}>
                            <Col>
                                <PageHeader
                                style={{ width: "100%", paddingRight: 16, paddingLeft: 10 }}
                                onBack={() => {
                                    // key unique to the location, only present if we came to this page from another first using React Router. ex. clicking details in BinDetails.
                                    // https://reactrouter.com/en/main/start/concepts#locations
                                    const hasHistory = !(location?.key == null);
                                    if (!hasHistory) {
                                        // going to the homepage is the best we can do currently (no knowledge of prior history or upper route/component to go back to)
                                        history.push(Routes.HOME_ROUTE);
                                    } else {
                                        history.goBack();
                                    }
                                }}
                                title={binInfo?.name}
                                tags={getPremierPlusBinDtoQuery.isLoading ?  [] : headerTags }
                                />
                            </Col>

                            {
                                !getPremierPlusBinDtoQuery.isLoading && 
                                <>
                                    <Col>
                                    {
                                        getPremierPlusBinDtoQuery.data?.moduleConnected ? 
                                        <CheckCircleTwoTone twoToneColor="#52c41a" /> 
                                        : 
                                        <CloseCircleTwoTone twoToneColor="#eb2f96" />
                                    }
                                    </Col>
                                    <Col>
                                        <Typography.Text type="secondary">{`Last Status Reading: ${lastSnapshotCaptureTime != null ? lastSnapshotCaptureTime?.format(DATETIME_FORMAT) : '----'}`}</Typography.Text>
                                    </Col>
                                </>

                            }
                        </Row>                    
                    </Col>
                    <Col>
                        
                    </Col>
                    <Col>
                        <Row gutter={24}>
                            {isAdmin &&
                                <Col>
                                    <Button type='primary' onClick={handleClickDiagnosticsFile}>Diagnostics File</Button>
                                </Col>
                            }
                            <Col>
                                <Button type='primary' onClick={handleClickRefresh}><RedoOutlined />Refresh</Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                {
                    getPremierPlusBinDtoQuery.isLoading ? 
                    <Skeleton active />
                    :
                    <>
                        <Row justify={'space-around'} gutter={[16, 16]}>
                            <Col
                            xs={24}
                            xl={8}
                            style={{
                                justifyContent: "center",
                            }}>
                                <SystemControls binDto={getPremierPlusBinDtoQuery.data ?? null}/>
                            </Col>
                            <Col 
                            xs={24}
                            xl={8}
                            style={{ justifyContent: "center" }}>
                                <Flex flex="wrap" vertical justify={'center'}>
                                    <Card
                                        title="Last Tape Reading"
                                        extra={
                                            <Flex gap="small">
                                                <Button onClick={handleOpenTapestripModal}>Tapestrip</Button>
                                                <Button onClick={handleOpenAlertModal}>Alerts</Button>
                                            </Flex>
                                        }
                                        style={{
                                            width: "100%",
                                        }}
                                        styles={{
                                            body: {
                                                padding: 0,
                                            }
                                        }}
                                    >
                                        <Overview layout="horizontal" binDto={getPremierPlusBinDtoQuery.data ?? null} showLastTapeReadingDate={true} />
                                    </Card> 
                                    <br/>
                                    <ReadingOnOffDisplay binId={getPremierPlusBinDtoQuery.data?.binId} />
                                </Flex>
                            </Col>
                            {/* <Col xs={24} xl={8}>
                                <Flex justify={'center'} flex="wrap">
                                    <ReadingOnOffDisplay />
                                </Flex>
                            </Col> */}
                            <Col xs={24} xl={8}>
                                <Flex justify={"center"}>
                                    <DryerSettingsCard binDto={getPremierPlusBinDtoQuery.data ?? null} />
                                </Flex>
                                <br/>
                                {getPremierPlusBinDtoQuery.data?.hasCamera && (
                                    <Card title="Bin Camera">
                                        <Row style={{ paddingTop: "16px" }} justify={"center"}>
                                            <Col>
                                                <CameraImage binId={getPremierPlusBinDtoQuery.data?.binId} />
                                            </Col>
                                        </Row>
                                    </Card>
                                )}
                            </Col>
                        </Row>
                        {/* { isAdmin &&
                            <>
                                <Row>
                                    <ScheduleSampledSnapshots
                                    binId={binInfo?.id}
                                    settings={getPremierPlusBinDtoQuery.data?.settings}
                                    />
                                </Row>
                                <AdminControlsCard 
                                automationType={AutomationType.PremierPlus}
                                showRuntimeAndSettingsButtons={false}
                                showLogDownload={false}
                                />
                            </> 
                        } */}
                        <Row>
                            <Col span={24}>
                                <Charts binId={getPremierPlusBinDtoQuery.data?.binId ?? 0}/>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <PremierPlusTapeStripTable binId={getPremierPlusBinDtoQuery.data?.binId ?? 0} setDateRange={setTapeStripDateRange} dateRange={EMPTY_DATE_RANGE} binDto={getPremierPlusBinDtoQuery.data}/>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                {binInfo.binLat && binInfo.binLng ? (
                                    <FillingForcast lat={binInfo.binLat} lng={binInfo.binLng} />
                                ) : (
                                    <Card title="Weather Forecast">
                                        <Result status="warning" title="Error Getting Weather Data" />
                                    </Card>
                                )}
                            </Col>
                        </Row>
                    </>
                }

                <Modal
                open={tapestripModalOpen}
                onCancel={handleCloseTapestripModal}
                width={'100%'}
                centered
                title={
                    <Row justify={'space-between'} style={{padding:'16px'}}>
                        <Col>
                            <Typography.Title level={4}>Tapestrip</Typography.Title>
                        </Col>
                        <Col>
                            <DatePicker.RangePicker
                            value={tapeStripDateRange}
                            onChange={(values) => setTapeStripDateRange(values ?? [null, null])}
                            allowClear
                            />
                        </Col>
                    </Row>
                }
                footer={null}
                >
                    <Row>
                        <Col span={24}>
                            <PremierPlusTapeStripTable 
                            binId={getPremierPlusBinDtoQuery.data?.binId ?? 0} 
                            setDateRange={setTapeStripDateRange} 
                            dateRange={tapeStripDateRange} 
                            binDto={getPremierPlusBinDtoQuery.data}/>
                        </Col>
                    </Row>
                </Modal>

                <Modal
                open={alertModalOpen}
                onCancel={handleCloseAlertModal}
                width={'100%'}
                centered
                title={
                    <Row justify={'space-between'} style={{padding:'16px'}}>
                        <Col>
                            <Typography.Title level={4}>Alerts</Typography.Title>
                        </Col>
                        <Col>
                            <i>Alerts only update when system is in an active operation</i>
                        </Col>
                        <Col>
                            <Row justify={'end'} gutter={8} align='middle'>
                                <Col>
                                    <Radio.Group onChange={(e) => setAlertFilter(e.target.value as 1 | 2)} value={alertFilter}>
                                        <Radio.Button value={1}>Active</Radio.Button>
                                        <Radio.Button value={2}>History</Radio.Button>
                                    </Radio.Group>
                                </Col>
                                    <Col style={{visibility: alertFilter === 2 ? undefined : 'hidden'}}>
                                        <DatePicker.RangePicker
                                        value={alertDateRange}
                                        onChange={(values) => setAlertDateRange(values ?? [null, null])}
                                        allowClear
                                        />
                                    </Col>
                            </Row>
                        </Col>
                    </Row>
                }
                footer={null}>
                    <Row>
                        <Col span={24}>
                            <PremierPlusAlertTable 
                            binId={getPremierPlusBinDtoQuery.data?.binId ?? 0} 
                            binDto={getPremierPlusBinDtoQuery.data}
                            dateRange={alertDateRange}
                            alertFilter={alertFilter}
                            />
                        </Col>
                    </Row>    
                </Modal>
            </Layout.Content>
        </>
    );
};

export default ControlPanel;